import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { CoachEntity } from "@modules/profile/coachProfile/coachEntity"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleLightEntity } from "@modules/programBundle/entities/programBundleEntity"
import { type UserProgramDetailsDto } from "@modules/programs/gateway/programDto"
import {
  ProgramStatus,
  ProgramSubscriptionStatus,
  ProgramType,
  ProgramVisibility,
  type SharedCoach,
} from "@modules/programs/types/programTypes"
import { type SharedCoachDto } from "@modules/sharedCoach/gateway/sharedCoachDtos"
import { type HUImage } from "@modules/types/imageTypes"

type UserProgramDetailsState = Omit<UserProgramDetailsEntity, "isSessionsProgramType" | "getProgramPriceForm">
export class UserProgramDetailsEntity {
  id: string
  name: string
  description: string
  image: HUImage | null
  owner: CoachEntity | null
  visibility: ProgramVisibility
  subscriptionStatus: ProgramSubscriptionStatus
  subscriptionCount: number
  price: PriceObjectEntity | null
  entranceCheckEnabled: boolean
  invitationLink: string | null
  defaultHideSessionContent: boolean
  status: ProgramStatus
  duration: number | null
  type: ProgramType
  sessionCount: number
  trialDuration: number | null
  scoreboardLocked: boolean
  programBundleSubscriptionId: string | null
  sharedCoaches: SharedCoach[] | null
  subscribed: boolean
  isEligibleToTrial: boolean | null
  programBundle: ProgramBundleLightEntity | null

  private constructor(program: UserProgramDetailsState) {
    this.id = program.id
    this.name = program.name
    this.description = program.description
    this.image = program.image
    this.owner = program.owner
    this.visibility = program.visibility
    this.subscriptionStatus = program.subscriptionStatus
    this.subscriptionCount = program.subscriptionCount
    this.price = program.price
    this.entranceCheckEnabled = program.entranceCheckEnabled
    this.invitationLink = program.invitationLink
    this.defaultHideSessionContent = program.defaultHideSessionContent
    this.status = program.status
    this.duration = program.duration
    this.type = program.type
    this.sessionCount = program.sessionCount
    this.trialDuration = program.trialDuration
    this.scoreboardLocked = program.scoreboardLocked
    this.programBundleSubscriptionId = program.programBundleSubscriptionId
    this.sharedCoaches = program.sharedCoaches
    this.subscribed = program.subscribed
    this.isEligibleToTrial = program.isEligibleToTrial
    this.programBundle = program.programBundle
  }

  static fromDto(dto: UserProgramDetailsDto): UserProgramDetailsEntity {
    return new UserProgramDetailsEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      owner: dto.owner ? CoachEntity.fromDto(dto.owner) : null,
      visibility: dto.visibility,
      subscriptionStatus: dto.subscriptionStatus,
      subscriptionCount: dto.subscriptionCount,
      price: dto.price ? PriceObjectEntity.fromDto(dto.price) : null,
      entranceCheckEnabled: dto.entranceCheckEnabled,
      invitationLink: dto.invitationLink,
      defaultHideSessionContent: dto.defaultHideSessionContent,
      status: dto.status,
      duration: dto.duration ?? null,
      type: dto.type,
      sessionCount: dto.sessionCount,
      trialDuration: dto.trialDuration ?? null,
      scoreboardLocked: dto.scoreboardVisibility ? false : true,
      programBundleSubscriptionId: dto.programBundleSubscriptionId ?? null,
      sharedCoaches: dto.sharedCoaches
        ? dto.sharedCoaches.map((sc: SharedCoachDto) => ({
            role: sc.role,
            status: sc.status,
            user: UserEntity.fromDto(sc.user),
          }))
        : null,
      subscribed: dto.subscribed,
      isEligibleToTrial: dto.isEligibleToTrial ?? null,
      programBundle: dto.programBundle
        ? new ProgramBundleLightEntity({
            programBundleId: dto.programBundle.programBundleId,
            programBundleName: dto.programBundle.programBundleName,
            paidPrice: dto.programBundle.paidPrice,
            type: ProgramBundleType.VIRTUAL, // or ON_OFF, you may need to infer this from context
          })
        : null,
    })
  }

  static isSessionsProgramType(programType: ProgramType) {
    return programType === ProgramType.VIRTUAL || programType === ProgramType.PRO || programType === ProgramType.ONOFF
  }

  getProgramPriceForm(defaultCurrency?: string) {
    return {
      value: this.price?.value,
      currency: this.price?.currency ?? defaultCurrency ?? "EUR",
      addFreeTrial: !!this.trialDuration,
      trialDuration: this.trialDuration ?? null,
    }
  }
}
